/**
 * Modified for HDMX Super sku
 */

import BuyByColor from '@/components/blocks/ProductDetails/parts/buyByColor/BuyByColor';
import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import {
	BASE_COLOR_IDENTIFIER_SUFFIX,
	PDP_SUPERSKU_HELP_ESPOT,
} from '@/components/content/ProductDetails/constants';
import { ProductAttributeV2 } from '@/components/content/ProductDetails/parts/AttributeV2';
import { PaperCard } from '@/components/content/ProductDetails/parts/PaperCards';
import { attributesContainerSx } from '@/components/content/ProductDetails/styles/attributes';
import { cardTitleSX } from '@/components/content/ProductDetails/styles/cardTitle';
import { USAGE_DEFINING } from '@/data/constants/catalog';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { getAttrsByIdentifier, useProductDetails } from '@/data/Content/ProductDetails';
import { usePaintColors } from '@/data/Content/usePaintColors';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { isB2BStore, useSettings } from '@/data/Settings';
import { ProductBaseColor } from '@/data/types/ColorType';
import { ProductType } from '@/data/types/Product';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
import { getIsProductExcluded } from '@/data/utils/isProductExcluded';
import { getBaseColorDataByProduct } from '@/utils/colorAndPriceUtils';
import { Stack, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import { FC, useContext, useMemo } from 'react';

// eslint-disable-next-line complexity
export const ProductDetailsAttributes: FC = () => {
	const { selection, product, availability, onAttributeChange } = useContext(
		ContentContext
	) as ReturnType<typeof useProductDetails>;

	const { colorClassification } = usePaintColors();
	const { settings } = useSettings();
	const { physicalStoreId: physicalStoreIdentifier } = useDefaultStore();
	const { SelectYourOptions } = useLocalization('productDetail');

	const isB2B = isB2BStore(settings);

	/**
	 * Check if the product is excluded for purchase in the current store or market
	 * @param item
	 * @returns true, if this product is excluded in this store or market.
	 */
	const isProductExcluded = (item: ProductType) =>
		getIsProductExcluded({
			product: item,
			physicalStoreId: physicalStoreIdentifier,
			isB2B,
		});
	/**
	 * Restructure the availability into a map,
	 * to represent if an item has inventory or not.
	 * <Item.id1: true >
	 * <Item.id2: false >
	 * ...
	 */
	const availabilityMap = useMemo(() => {
		const availabilityMap = new Map<string, boolean>();

		availability?.forEach((item: ProductAvailabilityData) => {
			item?.productId &&
				availabilityMap.set(
					item?.productId,
					item.inventoryStatus === 'Available' &&
						(Number(item?.availableQuantity) > 0 ||
							Number(item?.x_AvailableQuantityInMarket) > 0 ||
							Number(item?.x_customField2) > 0)
				);
		});
		return availabilityMap;
	}, [availability]);

	const totalAvailableQuantity = useMemo(() => {
		const trueCount = Array.from(availabilityMap.values()).filter((value) => value).length;
		return trueCount;
	}, [availabilityMap]);

	const isSuperSKU =
		product?.items &&
		product.items.length > 0 &&
		product.partNumber.endsWith('_SS') &&
		product?.definingAttributes &&
		product.definingAttributes.length > 0;
	if (!isSuperSKU) return null;

	// For Super SKU only.
	const superSKUData = product;

	// const superSKU
	const superSkuPrefix = superSKUData.partNumber.split('_')[0];
	const brand = superSKUData?.manufacturer ?? '';
	// Only select the defining attributes that belong to this super SKU
	const superSkuAttributes: any[] = superSKUData.definingAttributes.filter((attr) =>
		attr.identifier.endsWith(superSkuPrefix)
	);

	// Based on the current product brand and defining attributes, check if it has base color
	const baseColorInfo: ProductBaseColor = getBaseColorDataByProduct(colorClassification, {
		manufacturer: brand,
		attributes: selection?.sku?.definingAttributes || product.definingAttributes,
	});

	const numAttrs = superSkuAttributes?.length ?? 0;
	const skusAsAttrsList =
		product?.items?.map((sku) => getAttrsByIdentifier(sku.definingAttributes)) ?? [];
	const shouldRenderAttributes = totalAvailableQuantity > 1 && numAttrs > 0;

	return (
		<>
			{shouldRenderAttributes ? (
				<PaperCard id="superSkuCard">
					<Typography variant="headingSegment" sx={cardTitleSX}>
						{SelectYourOptions.t()}
					</Typography>
					<Stack gap={1.25} justifyContent="space-around" sx={attributesContainerSx}>
						{superSkuAttributes?.map((attribute, index) => {
							const isHiddenByBase =
								attribute.identifier.includes(BASE_COLOR_IDENTIFIER_SUFFIX) &&
								attribute.usage === USAGE_DEFINING;
							// check if at least one value of this attribute is available in inventory
							//  when combined with the current selection of other attributes, and not excluded
							//  for purchase in this store or market.
							// If none of the values is available, the entire attribute is disabled.
							//  (unless it's the pivot attribute, which is always enabled)
							const isAnyValueAvailable = attribute.values.some((value: any) => {
								const potentialSelection = {
									...selection.attrsByIdentifier,
									[attribute.identifier]: value.identifier,
								};
								const matchingItems =
									product?.items?.filter((item) => {
										const itemAttrs = getAttrsByIdentifier(item.definingAttributes);
										return isEqual(itemAttrs, potentialSelection);
									}) ?? [];
								return matchingItems.some(
									(item) => availabilityMap.get(item.id) && !isProductExcluded(item)
								);
							});
							return (
								<ProductAttributeV2
									key={attribute.identifier}
									availability={availability}
									attribute={attribute}
									skusAsAttrsList={skusAsAttrsList}
									onAttributeChange={onAttributeChange}
									selection={selection}
									isPivot={!!(index === 0 && numAttrs > 1)} // make the first attribute the pivot
									forceDisabled={!isAnyValueAvailable}
									isHiddenByBase={isHiddenByBase}
								/>
							);
						})}
					</Stack>
					<EMarketingSpot
						id={PDP_SUPERSKU_HELP_ESPOT}
						properties={{ emsName: PDP_SUPERSKU_HELP_ESPOT }}
					/>
				</PaperCard>
			) : null}

			{baseColorInfo.hasBaseColor ? (
				<BuyByColor
					selection={selection}
					product={product}
					baseColorInfo={baseColorInfo}
					brand={brand}
					partNumber={selection?.sku?.partNumber ?? ''}
					onAttributeChange={onAttributeChange}
				/>
			) : null}
		</>
	);
};
